import React, { useState, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Link } from 'react-router-dom';
import {
  FaConciergeBell,
  FaMoneyBillAlt,
  FaSearch,
  FaFilter,
  FaChevronDown,
  FaChevronUp,
} from 'react-icons/fa';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

function RoomServicePage() {
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [items, setItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState(null);
  const [activeService, setActiveService] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    isActive: true,
    hasItems: 'all',
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const categoriesResponse = await axios.get(
          `${BQ_BASE_URL}/bq/api/room-service-categories/`
        );
        const servicesResponse = await axios.get(`${BQ_BASE_URL}/bq/api/room-service-services/`);
        const itemsResponse = await axios.get(`${BQ_BASE_URL}/bq/api/room-service-items/`);

        setCategories(categoriesResponse.data);
        setServices(servicesResponse.data);
        setItems(itemsResponse.data);
      } catch (error) {
        toast.error('Failed to fetch data');
      }
    };

    fetchData();
  }, []);

  const getServicesForCategory = (categoryId) => {
    return services.filter((service) => service.categoryid === categoryId);
  };

  const getItemsForService = (serviceId) => {
    return items.filter((item) => item.serviceid === serviceId);
  };

  const toggleCategory = (categoryId) => {
    setActiveCategory(activeCategory === categoryId ? null : categoryId);
  };

  const toggleService = (serviceId) => {
    setActiveService(activeService === serviceId ? null : serviceId);
  };

  // Filter categories based on search term and active status
  const filteredCategories = categories.filter((category) => {
    const matchesSearch = category.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesActiveStatus = filters.isActive ? category.isactive : true;
    return matchesSearch && matchesActiveStatus;
  });

  // Filter services based on the hasItems filter
  const filterServices = (servicesToFilter) => {
    return servicesToFilter.filter((service) => {
      const isActiveMatch = filters.isActive ? service.isavailable : true;
      const serviceItems = getItemsForService(service.id);
      const hasItemsMatch =
        filters.hasItems === 'all' ||
        (filters.hasItems === 'with' && serviceItems.length > 0) ||
        (filters.hasItems === 'without' && serviceItems.length === 0);
      return isActiveMatch && hasItemsMatch;
    });
  };

  // Toggle component for Food Related status
  const FoodRelatedToggle = ({ isRelatedToFood }) => {
    return (
      <div className="flex items-center justify-center">
        <div
          className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors ${
            isRelatedToFood ? 'bg-black' : 'bg-gray-300'
          }`}
        >
          <span
            className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform ${
              isRelatedToFood ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="bg-gray-100 p-6 relative min-h-screen">
      <header className="flex justify-between items-center mb-3 bg-white p-3">
        <h1 className="text-black text-xl font-bold ml-2">Amenities Management</h1>
        <div className="flex space-x-6">
          <button
            onClick={() => navigate('/dashboard/mdm/amenities-management/room-service')}
            className="btn text-black font-bold"
          >
            Room Service
          </button>
          <button
            onClick={() => navigate('/dashboard/mdm/amenities-management/amenities')}
            className="btn "
          >
            Amenities
          </button>
        </div>
      </header>

      {/* Main Content */}
      <div className="ml-1 p-6 w-full bg-white">
        <h1 className="text-xl font-bold">Room Services</h1>
        <p className="text-sm">Indulge in Comfort: 24/7 Room Service at Your Fingertips</p>
        <div className="mt-6 bg-gray-50 p-4 rounded-lg shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-4">
            {/* Search Input */}
            <div className="relative w-96">
              <FaSearch className="absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                placeholder="Search services..."
                className="pl-10 pr-4 py-2 w-full border rounded-lg"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Buttons on the right */}
            <div className="flex items-center space-x-3 ml-auto">
              <Link to="/dashboard/mdm/amenities-management/room-service/manage-room-categories">
                <button className="px-10 py-2 bg-black text-white rounded hover:bg-black hover:bg-black">
                  Manage Room Services
                </button>
              </Link>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className="px-10 py-2 bg-black text-white rounded hover:bg-black hover:bg-black flex items-center"
              >
                <FaFilter className="mr-2" />
                <span>Filters</span>
              </button>
            </div>
          </div>
        </div>

        {/* Filter Section */}
        {showFilters && (
          <div className="bg-gray-50 p-4 rounded-md shadow-inner mt-2">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Items Availability</label>
                <select
                  className="w-full p-2 border rounded"
                  value={filters.hasItems}
                  onChange={(e) => setFilters({ ...filters, hasItems: e.target.value })}
                >
                  <option value="all">All Services</option>
                  <option value="with">With Items</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Main Table */}
        <div className="overflow-x-auto mt-4">
          <table className="min-w-full bg-white border rounded-lg overflow-hidden">
            <thead className="bg-gray-100">
              <tr>
                <th className="py-3 px-4 text-left w-1/5">Category</th>
                <th className="py-3 px-4 text-left w-1/5">Sub-Category</th>
                <th className="py-3 px-4 text-left w-1/5">Items</th>
                <th className="py-3 px-4 text-left w-1/5">Related to food service</th>
                <th className="py-3 px-4 text-left w-1/5">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredCategories.length > 0 ? (
                filteredCategories.map((category) => {
                  const categoryServices = getServicesForCategory(category.id);
                  const filteredServices = filterServices(categoryServices);

                  // Only show category if it has services after filtering (unless showing all)
                  if (filteredServices.length === 0 && filters.hasItems !== 'all') {
                    return null;
                  }

                  return (
                    <React.Fragment key={category.id}>
                      <tr className="cursor-pointer" onClick={() => toggleCategory(category.id)}>
                        <td className="py-4 px-4">
                          <div className="flex items-center">
                            <img
                              src={category.imageurl}
                              alt={category.name}
                              className="w-10 h-10 rounded-full object-cover mr-3"
                            />
                            <div>
                              <p className="font-medium">{category.name}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-4">{filteredServices.length} services</td>
                        <td className="py-4 px-4">
                          {filteredServices.reduce(
                            (acc, service) => acc + getItemsForService(service.id).length,
                            0
                          )}{' '}
                          items
                        </td>
                        <td className="py-4 px-4">
                          <FoodRelatedToggle isRelatedToFood={category.isrelatedtofood} />
                        </td>
                        <td className="py-4 px-4">
                          <span
                            className={`px-2 py-1 rounded-full text-xs ${
                              category.isactive
                                ? 'bg-gray-100 text-gray-800'
                                : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {category.isactive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                      </tr>

                      {activeCategory === category.id &&
                        filteredServices.map((service) => (
                          <React.Fragment key={service.id}>
                            <tr
                              className="bg-gray-50 cursor-pointer"
                              onClick={() => toggleService(service.id)}
                            >
                              <td className="py-3 px-4 pl-12">
                                <div className="flex items-center">
                                  <div className="ml-2">
                                    <p className="font-medium">{service.name}</p>
                                    <p className="text-sm text-gray-500">
                                      Duration: {service.duration} hrs
                                    </p>
                                  </div>
                                  {activeService === service.id ? (
                                    <FaChevronUp className="ml-2" />
                                  ) : (
                                    <FaChevronDown className="ml-2" />
                                  )}
                                </div>
                              </td>
                              <td className="py-3 px-4"></td>
                              <td className="py-3 px-4">
                                {getItemsForService(service.id).length} items
                              </td>
                              <td className="py-3 px-4">
                                {/* Empty for service rows as food related is at category level */}
                              </td>
                              <td className="py-3 px-4">
                                <span
                                  className={`px-2 py-1 rounded-full text-xs ${
                                    service.isavailable
                                      ? 'bg-green-100 text-green-800'
                                      : 'bg-red-100 text-red-800'
                                  }`}
                                >
                                  {service.isavailable ? 'Available' : 'Unavailable'}
                                </span>
                              </td>
                            </tr>

                            {activeService === service.id &&
                              getItemsForService(service.id).length > 0 && (
                                <tr className="bg-gray-100">
                                  <td colSpan="5" className="py-3 px-4 pl-20">
                                    <div className="grid grid-cols-3 gap-6">
                                      {getItemsForService(service.id).map((item) => (
                                        <div
                                          key={item.id}
                                          className="bg-white p-5 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 ease-in-out transform hover:scale-105"
                                        >
                                          <div className="space-y-3">
                                            <p className="font-semibold text-lg text-gray-800">
                                              {item.name}
                                            </p>
                                            <div className="space-y-2">
                                              <p className="text-sm text-gray-600">
                                                Base Price: ₹{item.baseprice}
                                              </p>
                                              <p className="text-sm text-gray-600">
                                                Urgent Price: ₹{item.urgentprice}
                                              </p>
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </td>
                                </tr>
                              )}
                          </React.Fragment>
                        ))}
                    </React.Fragment>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="5" className="py-6 text-center text-gray-500">
                    No categories found matching your criteria
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <ToastContainer />
      </div>
    </div>
  );
}

export default RoomServicePage;

// import React, { useState, useEffect } from "react";
// import { ToastContainer, toast } from "react-toastify";
// import "react-toastify/dist/ReactToastify.css";
// import { Link } from "react-router-dom";
// import {
//   FaConciergeBell,
//   FaMoneyBillAlt,
//   FaSearch,
//   FaFilter,
//   FaChevronDown,
//   FaChevronUp,
// } from "react-icons/fa";
// import axios from "axios";
// import { useNavigate } from "react-router-dom";
// const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// function RoomServicePage() {
//   const [categories, setCategories] = useState([]);
//   const [services, setServices] = useState([]);
//   const [items, setItems] = useState([]);
//   const [searchTerm, setSearchTerm] = useState("");
//   const [activeCategory, setActiveCategory] = useState(null);
//   const [activeService, setActiveService] = useState(null);
//   const [showFilters, setShowFilters] = useState(false);
//   const navigate = useNavigate();
//   const [filters, setFilters] = useState({
//     isActive: true,
//     hasItems: "all",
//   });

//   useEffect(() => {
//     const fetchData = async () => {
//       try {
//         const categoriesResponse = await axios.get(
//           `${BQ_BASE_URL}/bq/api/room-service-categories/`
//         );
//         const servicesResponse = await axios.get(
//           `${BQ_BASE_URL}/bq/api/room-service-services/`
//         );
//         const itemsResponse = await axios.get(
//           `${BQ_BASE_URL}/bq/api/room-service-items/`
//         );

//         setCategories(categoriesResponse.data);
//         setServices(servicesResponse.data);
//         setItems(itemsResponse.data);
//       } catch (error) {
//         toast.error("Failed to fetch data");
//       }
//     };

//     fetchData();
//   }, []);

//   const getServicesForCategory = (categoryId) => {
//     return services.filter((service) => service.categoryid === categoryId);
//   };

//   const getItemsForService = (serviceId) => {
//     return items.filter((item) => item.serviceid === serviceId);
//   };

//   const toggleCategory = (categoryId) => {
//     setActiveCategory(activeCategory === categoryId ? null : categoryId);
//   };

//   const toggleService = (serviceId) => {
//     setActiveService(activeService === serviceId ? null : serviceId);
//   };

//   // Filter categories based on search term and active status
//   const filteredCategories = categories.filter((category) => {
//     const matchesSearch = category.name
//       .toLowerCase()
//       .includes(searchTerm.toLowerCase());
//     const matchesActiveStatus = filters.isActive ? category.isactive : true;
//     return matchesSearch && matchesActiveStatus;
//   });

//   // Filter services based on the hasItems filter
//   const filterServices = (servicesToFilter) => {
//     return servicesToFilter.filter((service) => {
//       const isActiveMatch = filters.isActive ? service.isavailable : true;
//       const serviceItems = getItemsForService(service.id);
//       const hasItemsMatch =
//         filters.hasItems === "all" ||
//         (filters.hasItems === "with" && serviceItems.length > 0) ||
//         (filters.hasItems === "without" && serviceItems.length === 0);
//       return isActiveMatch && hasItemsMatch;
//     });
//   };

//   return (
//     <div className="bg-gray-100 p-6 relative min-h-screen">
//       {/* <div className="w-1/4 bg-gray-100 text-black p-4 text-base font-semibold">
//         <ul className="space-y-4 mt-4">
//           <li className="bg-black text-white p-2 rounded flex items-center space-x-2">
//             <FaConciergeBell className="text-lg" />
//             <Link to="/dashboard/mdm/amenities-management/room-service">
//               Room Service
//             </Link>
//           </li>
//           <li className="hover:bg-gray-200 p-2 rounded flex items-center space-x-2">
//             <FaMoneyBillAlt className="text-lg" />
//             <Link to="/dashboard/mdm/amenities-management/amenities">
//               Amenities
//             </Link>
//           </li>
//         </ul>
//       </div> */}

//       <header className="flex justify-between items-center mb-3 bg-white p-3">
//         <h1 className="text-black text-xl font-bold ml-2">
//           Amenities Managment
//         </h1>
//         <div className="flex space-x-6">
//           <button
//             onClick={() =>
//               navigate("/dashboard/mdm/amenities-management/room-service")
//             }
//             className="btn text-black font-bold"
//           >
//             Room Service
//           </button>
//           <button
//             onClick={() =>
//               navigate("/dashboard/mdm/amenities-management/amenities")
//             }
//             className="btn "
//           >
//             Amenities
//           </button>
//         </div>
//       </header>

//       {/* Main Content */}
//       <div className="ml-1 p-6 w-full bg-white">
//         <h1 className="text-xl font-bold">Room Services</h1>
//         <p className="text-sm">
//           Indulge in Comfort: 24/7 Room Service at Your Fingertips
//         </p>
//         <div className="mt-6 bg-gray-50 p-4 rounded-lg shadow-sm">

//           <div className="flex flex-wrap items-center justify-between gap-4">
//             {/* Search Input */}
//             <div className="relative w-96">
//               <FaSearch className="absolute left-3 top-3 text-gray-400" />
//               <input
//                 type="text"
//                 placeholder="Search services..."
//                 className="pl-10 pr-4 py-2 w-full border rounded-lg"
//                 value={searchTerm}
//                 onChange={(e) => setSearchTerm(e.target.value)}
//               />
//             </div>

//             {/* Buttons on the right */}
//             <div className="flex items-center space-x-3 ml-auto">
//               <Link to="/dashboard/mdm/amenities-management/room-service/manage-room-categories">
//                 <button className="px-10 py-2 bg-black text-white rounded hover:bg-black hover:bg-black">
//                   Manage Room Services
//                 </button>
//               </Link>

//               <button
//                 onClick={() => setShowFilters(!showFilters)}
//                 className="px-10 py-2 bg-black text-white rounded hover:bg-black hover:bg-black flex items-center"
//               >
//                 <FaFilter className="mr-2" />
//                 <span>Filters</span>
//               </button>
//             </div>
//           </div>
//         </div>

//         {/* Filter Section */}
//         {showFilters && (
//           <div className="bg-gray-50 p-4 rounded-md shadow-inner mt-2">
//             <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
//               {/* <div>
//                 <label className="block text-sm font-medium mb-1">
//                   Active Status
//                 </label>
//                 <select
//                   className="w-full p-2 border rounded"
//                   value={filters.isActive}
//                   onChange={(e) =>
//                     setFilters({
//                       ...filters,
//                       isActive: e.target.value === "true",
//                     })
//                   }
//                 >
//                   <option value={true}>Active Only</option>
//                   <option value={false}>All Status</option>
//                 </select>
//               </div> */}
//               <div>
//                 <label className="block text-sm font-medium mb-1">
//                   Items Availability
//                 </label>
//                 <select
//                   className="w-full p-2 border rounded"
//                   value={filters.hasItems}
//                   onChange={(e) =>
//                     setFilters({ ...filters, hasItems: e.target.value })
//                   }
//                 >
//                   <option value="all">All Services</option>
//                   <option value="with">With Items</option>
//                   {/* <option value="without">Without Items</option> */}
//                 </select>
//               </div>
//             </div>
//           </div>
//         )}

//         {/* Main Table */}
//         <div className="overflow-x-auto mt-4">
//           <table className="min-w-full bg-white border rounded-lg overflow-hidden">
//             <thead className="bg-gray-100">
//               <tr>
//                 <th className="py-3 px-4 text-left w-1/4">Category</th>
//                 <th className="py-3 px-4 text-left w-1/4">Sub-Category</th>
//                 <th className="py-3 px-4 text-left w-1/4">Items</th>
//                 <th className="py-3 px-4 text-left w-1/4">Details</th>
//               </tr>
//             </thead>
//             <tbody className="divide-y divide-gray-200">
//               {filteredCategories.length > 0 ? (
//                 filteredCategories.map((category) => {
//                   const categoryServices = getServicesForCategory(category.id);
//                   const filteredServices = filterServices(categoryServices);

//                   // Only show category if it has services after filtering (unless showing all)
//                   if (
//                     filteredServices.length === 0 &&
//                     filters.hasItems !== "all"
//                   ) {
//                     return null;
//                   }

//                   return (
//                     <React.Fragment key={category.id}>
//                       <tr
//                         className="cursor-pointer"
//                         onClick={() => toggleCategory(category.id)}
//                       >
//                         <td className="py-4 px-4">
//                           <div className="flex items-center">
//                             <img
//                               src={category.imageurl}
//                               alt={category.name}
//                               className="w-10 h-10 rounded-full object-cover mr-3"
//                             />
//                             <div>
//                               <p className="font-medium">{category.name}</p>
//                             </div>
//                           </div>
//                         </td>
//                         <td className="py-4 px-4">
//                           {filteredServices.length} services
//                         </td>
//                         <td className="py-4 px-4">
//                           {filteredServices.reduce(
//                             (acc, service) =>
//                               acc + getItemsForService(service.id).length,
//                             0
//                           )}{" "}
//                           items
//                         </td>
//                         <td className="py-4 px-4">
//                           <span
//                             className={`px-2 py-1 rounded-full text-xs ${category.isactive
//                               ? "bg-gray-100 text-gray-800"
//                               : "bg-red-100 text-red-800"
//                               }`}
//                           >
//                             {category.isactive ? "Active" : "Inactive"}
//                           </span>
//                         </td>
//                       </tr>

//                       {activeCategory === category.id &&
//                         filteredServices.map((service) => (
//                           <React.Fragment key={service.id}>
//                             <tr
//                               className="bg-gray-50 cursor-pointer"
//                               onClick={() => toggleService(service.id)}
//                             >
//                               <td className="py-3 px-4 pl-12">
//                                 <div className="flex items-center">
//                                   <div className="ml-2">
//                                     <p className="font-medium">
//                                       {service.name}
//                                     </p>
//                                     <p className="text-sm text-gray-500">
//                                       Duration: {service.duration} hrs
//                                     </p>
//                                   </div>
//                                   {activeService === service.id ? (
//                                     <FaChevronUp className="ml-2" />
//                                   ) : (
//                                     <FaChevronDown className="ml-2" />
//                                   )}
//                                 </div>
//                               </td>
//                               <td className="py-3 px-4"></td>
//                               <td className="py-3 px-4">
//                                 {getItemsForService(service.id).length} items
//                               </td>
//                               <td className="py-3 px-4">
//                                 <span
//                                   className={`px-2 py-1 rounded-full text-xs ${service.isavailable
//                                     ? "bg-green-100 text-green-800"
//                                     : "bg-red-100 text-red-800"
//                                     }`}
//                                 >
//                                   {service.isavailable
//                                     ? "Available"
//                                     : "Unavailable"}
//                                 </span>
//                               </td>
//                             </tr>

//                             {activeService === service.id &&
//                               getItemsForService(service.id).length > 0 && (
//                                 <tr className="bg-gray-100">
//                                   <td colSpan="4" className="py-3 px-4 pl-20">
//                                     <div className="grid grid-cols-3 gap-6">
//                                       {getItemsForService(service.id).map(
//                                         (item) => (
//                                           <div
//                                             key={item.id}
//                                             className="bg-white p-5 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 ease-in-out transform hover:scale-105"
//                                           >
//                                             <div className="space-y-3">
//                                               <p className="font-semibold text-lg text-gray-800">
//                                                 {item.name}
//                                               </p>
//                                               <div className="space-y-2">
//                                                 <p className="text-sm text-gray-600">
//                                                   Base Price: ₹{item.baseprice}
//                                                 </p>
//                                                 <p className="text-sm text-gray-600">
//                                                   Urgent Price: ₹
//                                                   {item.urgentprice}
//                                                 </p>
//                                               </div>
//                                             </div>
//                                           </div>
//                                         )
//                                       )}
//                                     </div>
//                                   </td>
//                                 </tr>
//                               )}
//                           </React.Fragment>
//                         ))}
//                     </React.Fragment>
//                   );
//                 })
//               ) : (
//                 <tr>
//                   <td colSpan="4" className="py-6 text-center text-gray-500">
//                     No categories found matching your criteria
//                   </td>
//                 </tr>
//               )}
//             </tbody>
//           </table>
//         </div>

//         <ToastContainer />
//       </div>
//     </div>
//   );
// }

// export default RoomServicePage;
